import classNames from "classnames";
import * as Icons from "Components/Icons";
import debounce from "lodash.debounce";
import React, { ChangeEvent, PropsWithChildren, useCallback, useEffect, useState } from "react";
import { Control, Controller, FieldPath, FieldPathValue, FieldValues } from "react-hook-form";
import { Button, Input, InputProps } from "Components/ReactstrapSlim";

const MIN_CAPACITY_VALUE = 1;
const MAX_CAPACITY_VALUE = 999;

const maskValue = (initialValue: string | number | readonly string[] | undefined): number => {
   let value: number = MIN_CAPACITY_VALUE;
   if (typeof initialValue == "string")
      value = Number.parseInt(initialValue);
   else if (typeof initialValue == "number")
      value = initialValue;

   if (!value || Number.isNaN(value))
      value = MIN_CAPACITY_VALUE;
   value = Math.max(value, MIN_CAPACITY_VALUE);
   value = Math.min(value, MAX_CAPACITY_VALUE);

   return value;
}

export type CapacityInputProps = InputProps & {
   onCapacityChange: (capacity: number) => void;
   debounceMiliseconds?: number;
}

export const CapacityInput: React.FC<CapacityInputProps> = ({ type, onCapacityChange, debounceMiliseconds, onChange, value, ...rest }) => {
   const [capacity, setCapacity] = useState<number>(maskValue(value));
   const [skipDebounce, setSkipDebounce] = useState<boolean>(true);
   const debouncedCapacityChange = useCallback(debounce(onCapacityChange, debounceMiliseconds), []);

   const increaseCapacity = () => {
      setCapacity((currentCapacity) => maskValue(currentCapacity + 1));
   }

   const decreaseCapacity = () => {
      setCapacity((currentCapacity) => maskValue(currentCapacity - 1));
   }

   const wrappedOnChange = (event: ChangeEvent<HTMLInputElement>) => {
      const newCapacity = maskValue(event.target.value);
      setCapacity(newCapacity);
   }

   useEffect(() => {
      if (onCapacityChange) {
         if (!skipDebounce && debounceMiliseconds) {
            debouncedCapacityChange(capacity);
         }
         else {
            onCapacityChange(capacity);
            setSkipDebounce(false);
         }
      }
   }, [capacity])

   useEffect(() => {
      const maskedValue = maskValue(value);
      if (capacity != maskedValue) {
         setSkipDebounce(true);
         setCapacity(maskedValue);
      }
   }, [value])

   return (
      <div className={classNames("capacity-input", { [`capacity-input-${rest.bsSize}`]: !!rest.bsSize })}>
         <Button className="capacity-input-control capacity-input-control-decrease" type="button" color="transparent" onClick={decreaseCapacity} disabled={capacity == MIN_CAPACITY_VALUE || rest.disabled} aria-label="decrease capacity">
            <Icons.MinusCircled />
         </Button>
         <Input type="text" value={capacity} onChange={wrappedOnChange} {...rest} />
         <div className="capacity-input-icon">
            <Icons.Person />
         </div>
         <Button className="capacity-input-control capacity-input-control-increase" type="button" color="transparent" onClick={increaseCapacity} disabled={capacity == MAX_CAPACITY_VALUE || rest.disabled} aria-label="increase capacity">
            <Icons.PlusCircled />
         </Button>
      </div>
   )
}

export interface CapacityInputControlledProps<T extends FieldValues> extends InputProps {
   control: Control<T>,
   name: FieldPath<T>,
   debounceMiliseconds?: number;
}

export const CapacityInputControlled = <T extends FieldValues,>({ control, name, debounceMiliseconds, ...rest }: PropsWithChildren<CapacityInputControlledProps<T>>) => {
   return (
      <Controller
         name={name}
         control={control}
         render={({ field }) => <CapacityInput onCapacityChange={value => field.onChange(value as FieldPathValue<T, typeof name>)} value={field.value} debounceMiliseconds={debounceMiliseconds} {...rest} />}
      />
   );
}

//export const CapacityInputFL: React.FC<InputFLProps> = ({ children, type, formMethods, name, ...rest }) => {

//   const value = Number.parseInt(formMethods.watch(name));

//   const modifyValue = (increment: number) => {
//      let currentValue = Number.parseInt(formMethods.getValues(name));
//      if (!currentValue || currentValue == NaN)
//         currentValue = MIN_CAPACITY_VALUE;
//      let newValue = currentValue + increment;
//      newValue = Math.max(newValue, MIN_CAPACITY_VALUE);
//      newValue = Math.min(newValue, MAX_CAPACITY_VALUE);
//      formMethods.setValue(name, newValue);
//   }

//   const increaseCapacity = () => {
//      modifyValue(1);
//   }

//   const decreaseCapacity = () => {
//      modifyValue(-1);
//   }

//   useEffect(() => {
//      //Init value in case default wasn't set
//      modifyValue(0);
//   }, []);

//   return (
//      <Controller
//         name={name}
//         control={formMethods.control}
//         render={(props) => (
//            <Input value={props.value} type="text" {...rest} ref={field.ref} name={field.name} onBlur={field.onBlur} onChange={(e) => field.onChange(onChangeInternal(e.target.value))} className={classNames(rest.className, { "custom-select": rest.type == "select" })} />;
//        )}
//      />
//   );
//}
///*


//      <InputFL type="text" formMethods={formMethods} name={name} {...rest} >
//         <Icons.MinusCircled onClick={decreaseCapacity} className={classNames({ 'icon-disabled': value == MIN_CAPACITY_VALUE })} /> <Icons.PlusCircled onClick={increaseCapacity} className={classNames({ 'icon-disabled': value == MAX_CAPACITY_VALUE })} />
//      </InputFL>
// */